import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, CheckCircle2, TrendingUp, Clock } from "lucide-react";
import Star from "../common/Star";
import { useAIAgentData } from "../../hooks/useAIAgentData";
import "../../styles/animations.css";

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

// AI Chat Animation (hero) helper
function AIChatAnimation() {
  const { data } = useAIAgentData();
  const { chatSteps, conversation } = data.chatAnimation;

  const [msgVisible, setMsgVisible] = useState<boolean[]>([false, false, false, false]);
  const [typing, setTyping] = useState(false);
  const [captureVisible, setCaptureVisible] = useState(false);
  const [captureStep, setCaptureStep] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    function reset() {
      setMsgVisible([false, false, false, false]);
      setTyping(false);
      setCaptureVisible(false);
      setCaptureStep(0);
    }

    function run() {
      reset();
      chatSteps.forEach((s) => {
        const t = setTimeout(() => {
          if (s.type === "show-msg")      setMsgVisible(prev => { const n = [...prev]; n[s.index!] = true; return n; });
          if (s.type === "show-typing")   setTyping(true);
          if (s.type === "hide-typing")   setTyping(false);
          if (s.type === "show-capture")  setCaptureVisible(true);
          if (s.type === "capture-step")  setCaptureStep(s.value!);
        }, s.delay);
        timers.push(t);
      });
      // restart loop
      const loop = setTimeout(run, 13500);
      timers.push(loop);
    }

    run();
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="relative w-full max-w-[420px] mx-auto select-none">
      <div className="absolute -inset-6 bg-gradient-to-br from-[#1179F9]/20 to-[#A6A0F6]/10 rounded-3xl blur-2xl" />

      <div className="relative bg-[#0d0d2b]/90 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-[#1179F9]/10">
          <div className="relative">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1179F9] to-[#A6A0F6] flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-[#0d0d2b]" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold">NuStartz AI Agent</p>
            <p className="text-green-400 text-xs">Online · Replies instantly</p>
          </div>
          <div className="ml-auto flex gap-1.5">
            {[0, 1, 2].map(i => <span key={i} className="w-2.5 h-2.5 rounded-full bg-white/20" />)}
          </div>
        </div>

        {/* Messages */}
        <div className="p-5 space-y-3 min-h-[520px]">
          {conversation.map((msg, i) => (
            msgVisible[i] ? (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className={`flex ${msg.role === "visitor" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "ai" && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1179F9] to-[#A6A0F6] flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                    <Sparkles size={11} className="text-white" />
                  </div>
                )}
                <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === "visitor"
                    ? "bg-[#1179F9] text-white rounded-tr-sm"
                    : "bg-white/10 text-[#e0e0e0] rounded-tl-sm"
                }`}>
                  {msg.text}
                </div>
              </motion.div>
            ) : null
          ))}

          {/* Typing dots */}
          {typing && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1179F9] to-[#A6A0F6] flex items-center justify-center flex-shrink-0">
                <Sparkles size={11} className="text-white" />
              </div>
              <div className="bg-white/10 px-4 py-3 rounded-2xl rounded-tl-sm flex gap-1.5 items-center">
                {[0, 1, 2].map(d => (
                  <motion.span key={d} className="w-1.5 h-1.5 rounded-full bg-[#A6A0F6]"
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: d * 0.15 }}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Lead capture */}
          {captureVisible && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2 pt-1">
              <div className="flex gap-2 items-center">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1179F9] to-[#A6A0F6] flex items-center justify-center flex-shrink-0">
                  <Sparkles size={11} className="text-white" />
                </div>
                <div className="bg-white/10 px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm text-[#e0e0e0]">What's your name?</div>
              </div>
              {captureStep >= 1 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-end">
                  <div className="bg-[#1179F9] text-white px-4 py-2 rounded-2xl rounded-tr-sm text-sm">Alex Johnson</div>
                </motion.div>
              )}
              {captureStep >= 2 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2 items-center">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1179F9] to-[#A6A0F6] flex items-center justify-center flex-shrink-0">
                    <Sparkles size={11} className="text-white" />
                  </div>
                  <div className="bg-white/10 px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm text-[#e0e0e0]">And your email?</div>
                </motion.div>
              )}
              {captureStep >= 3 && (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex justify-end">
                  <div className="bg-green-500/20 border border-green-400/30 text-green-300 px-4 py-2 rounded-2xl text-sm flex items-center gap-2">
                    <CheckCircle2 size={14} /> Lead captured!
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </div>

        {/* Input bar */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5">
            <span className="text-white/30 text-sm flex-1">Type a message…</span>
            <div className="w-7 h-7 rounded-lg bg-[#1179F9] flex items-center justify-center">
              <ArrowRight size={13} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Floating chips */}
      <motion.div
        animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-4 -right-4 bg-[#1179F9] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
      >
        <TrendingUp size={12} /> 3× more leads
      </motion.div>
      <motion.div
        animate={{ y: [0, 6, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        className="absolute -bottom-4 -left-4 bg-[#A6A0F6]/20 border border-[#A6A0F6]/40 text-[#A6A0F6] text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
      >
        <Clock size={12} /> 24/7 active
      </motion.div>
    </div>
  );
}

export default function AIAgentHeroSection({ onDemoClick }: { onDemoClick: () => void }) {
  const { data } = useAIAgentData();
  const { badgeText, titlePart1, titlePart2, description, buttonText, bullets } = data.heroSection;

  return (
    <section className="relative px-5 sm:px-10 md:px-16 pt-10 pb-16 min-h-[80vh] flex">
      <Star />

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 justify-center items-center w-full">
        {/* Left */}
        <div className="space-y-8 lg:space-y-12 z-10">
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1179F9]/40 bg-[#1179F9]/10 text-[#A6A0F6] text-sm font-medium tracking-widest"
          >
            <Sparkles size={13} />
            {badgeText}
          </motion.div>

          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="text-4xl md:text-6xl xl:text-8xl font-bold leading-tight text-white tracking-wide"
          >
            <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent drop-shadow-[0px_4px_10px_rgba(255,255,255,0.25)]">
              {titlePart1}
            </span>
            <br />
            <span className="bg-gradient-to-r from-[#1179F9] to-[#A6A0F6] bg-clip-text text-transparent">
              {titlePart2}
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-xl lg:text-xl text-[#d9d9d9] leading-relaxed px-4 border-l-2 border-[#1179F9]"
          >
            {description}
          </motion.p>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="flex flex-wrap gap-4 items-center"
          >
            <button
              onClick={onDemoClick}
              className="relative inline-flex h-14 overflow-hidden rounded-xl p-[2px] focus:outline-none group"
            >
              <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#1179F9_0%,#A6A0F6_50%,#1179F9_100%)]" />
              <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-xl bg-[#050011] px-10 py-3 text-base font-semibold text-white backdrop-blur-3xl group-hover:bg-[#0a0020] transition-colors gap-2">
                {buttonText} <ArrowRight size={18} />
              </span>
            </button>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={4}
            className="flex flex-wrap gap-x-6 gap-y-2"
          >
            {bullets.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[#d9d9d9] text-base">
                <CheckCircle2 size={14} className="text-[#1179F9]" />
                {item}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right — AI Chat Animation */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={2}
          className="relative justify-center items-center order-last pt-10"
        >
          <AIChatAnimation />
        </motion.div>
      </div>
    </section>
  );
}
